import ScheduleEntry, { ScheduleEntryData } from '../models/ScheduleEntry';

type ScheduleEntryApiRow = Record<string, any>;

const API_FIELD_NAMES = new Set([
  'entry_type',
  'week_series',
  'start_time',
  'end_time',
  'subject_name',
  'teacher_name',
  'room_name',
  'publish_identity',
  'period_id',
  'group_ids',
]);

const compactIds = (values: unknown): number[] => {
  if (!Array.isArray(values)) return [];

  return values
    .map(value => (typeof value === 'object' && value !== null ? (value as any).id : value))
    .filter(value => value !== null && value !== undefined)
    .map(value => Number(value))
    .filter(value => !Number.isNaN(value));
};

const associationFromApi = (
  objectValue: unknown,
  id: unknown,
  name: unknown,
  extra: Record<string, unknown> = {}
) => {
  if (objectValue && typeof objectValue === 'object') return objectValue;
  if ((id === null || id === undefined) && !name) return undefined;

  return {
    ...(id !== null && id !== undefined ? { id } : {}),
    ...(name ? { name } : {}),
    ...extra,
  };
};

const collectAdditionalProps = (row: ScheduleEntryApiRow): Record<string, unknown> => {
  const additionalProps: Record<string, unknown> = {};

  Object.entries(row).forEach(([key, value]) => {
    if (
      API_FIELD_NAMES.has(key) ||
      [
        'id',
        'subject',
        'teacher',
        'teachers',
        'host',
        'groups',
        'room',
        'day',
        'time',
        'timeStart',
        'timeEnd',
        'weekSeries',
        'year',
        'exceptions',
        'title',
        'eventName',
        'description',
        'location',
        'notes',
        'type',
        'periodId',
        'publishIdentity',
        'teacher_id',
        'subject_id',
        'room_id',
        'source',
        'conflictsByWeek',
        'conflictLastCalculated',
        'additionalProps',
      ].includes(key)
    ) {
      return;
    }

    additionalProps[key] = value;
  });

  return additionalProps;
};

export const mapScheduleEntryFromApi = (row: ScheduleEntryApiRow = {}): ScheduleEntryData => {
  const type = row.entry_type ?? row.type;
  const subject = associationFromApi(row.subject, row.subject_id, row.subject_name, {
    color: row.subject_color,
  });
  const teacher = associationFromApi(row.teacher, row.teacher_id, row.teacher_name);
  const room = associationFromApi(row.room, row.room_id, row.room_name, {
    room_code: row.room_code,
  });

  return {
    id: row.id,
    subject: subject as ScheduleEntryData['subject'],
    course: row.course ?? row.subject_name,
    teacher: teacher as ScheduleEntryData['teacher'],
    teachers: row.teachers,
    host: row.host ?? (type === 'event' ? row.teacher_name : undefined),
    groups: row.groups ?? row.group_ids ?? [],
    room: room as ScheduleEntryData['room'],
    day: row.day,
    weekSeries: row.week_series ?? row.weekSeries ?? '',
    year: row.year,
    title: row.title,
    eventName: row.eventName ?? row.title,
    description: row.description,
    location: row.location,
    type,
    time: row.time,
    timeStart: row.start_time ?? row.timeStart,
    timeEnd: row.end_time ?? row.timeEnd,
    notes: row.notes,
    exceptions: row.exceptions,
    periodId: row.period_id ?? row.periodId ?? null,
    publishIdentity: row.publish_identity ?? row.publishIdentity ?? '',
    teacher_id: row.teacher_id,
    subject_id: row.subject_id,
    room_id: row.room_id,
    source: row.source,
    conflictsByWeek: row.conflictsByWeek,
    conflictLastCalculated: row.conflictLastCalculated,
    additionalProps: {
      ...(row.additionalProps || {}),
      ...collectAdditionalProps(row),
    },
  };
};

export const mapScheduleEntryToApi = (
  data: ScheduleEntry | ScheduleEntryData | Record<string, unknown>
): Record<string, unknown> => {
  const entry = data instanceof ScheduleEntry ? data : new ScheduleEntry(data as ScheduleEntryData);
  const teacherIds = compactIds(entry.teachers);
  const groupIds = compactIds(entry.groups);

  const payload: Record<string, unknown> = {
    entry_type: entry.type || 'class',
    week_series: entry.weekSeries,
    year: entry.year || new Date().getFullYear(),
    day: entry.day || null,
    start_time: entry.timeStart || null,
    end_time: entry.timeEnd || null,
    title: entry.title || null,
    description: entry.description || entry.notes || null,
    location: entry.location || null,
    exceptions: entry.exceptions ?? null,
  };

  if (entry.id) payload.id = entry.id;
  if (entry.periodId) payload.period_id = entry.periodId;
  else if (entry.additionalProps.timePeriodId) payload.period_id = entry.additionalProps.timePeriodId;
  if (entry.publishIdentity) payload.publish_identity = entry.publishIdentity;

  const subjectId = (entry.subjectObj as { id?: number })?.id;
  if (subjectId) payload.subject_id = subjectId;
  if (entry.subject) payload.subject_name = entry.subject;

  const roomId = (entry.roomObj as { id?: number })?.id;
  if (roomId) payload.room_id = roomId;
  payload.room_name = entry.room || null;

  if (entry.type === 'event') {
    if (entry.host) payload.teacher_name = entry.host;
  } else {
    const teacherId = (entry.teacherObj as { id?: number })?.id;
    if (teacherId) payload.teacher_id = teacherId;
    if (entry.teacher) payload.teacher_name = entry.teacher;
    if (teacherIds.length > 0) payload.teacher_ids = teacherIds;
  }

  if (groupIds.length > 0) {
    payload.groups = groupIds;
  } else if (Array.isArray(entry.additionalProps.group_ids)) {
    payload.group_ids = entry.additionalProps.group_ids;
  } else {
    payload.groups = [];
  }

  for (const key of ['client_id', 'entry_code', 'discard', 'draft_name'] as const) {
    if (entry.additionalProps[key] !== undefined) {
      payload[key] = entry.additionalProps[key];
    }
  }

  if (payload.day === '') payload.day = null;
  if (payload.start_time === '') payload.start_time = null;
  if (payload.end_time === '') payload.end_time = null;
  if (payload.room_name === '') payload.room_name = null;

  return payload;
};

